import {announce} from '@github-ui/aria-live'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSubmitSuggestedChangesMutation} from '@github-ui/pull-requests/use-submit-suggested-changes-mutation'
import {useOpenManageBatchPanel} from '@github-ui/pull-requests/use-open-manage-batch-panel'
import {Button} from '@primer/react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {useSetIsDialogOpen} from '../../contexts/DialogStateContext'

import type {SuggestedChange} from '../../conversation-types'
import {ApplyOrAddToBatch} from './CodeSuggestionActions/ApplyOrAddToBatch'
import {ApplySuggestionDialog} from './CodeSuggestionActions/ApplySuggestionDialog'
import {CodeSuggestionUnavailable} from './CodeSuggestionActions/CodeSuggestionUnavailable'
import {SUGGESTED_CHANGES_ERRORS, validateSuggestedChangeCanBeAddedToBatch} from '../../helpers/suggested-changes'
import {
  useComparisonInfo,
  useSuggestionBatch,
  useIsInUnifiedBatch,
  useThreadHasBatchedSuggestion,
  useUnifiedBatchActions,
} from '@github-ui/pull-requests/pull-request-store-selectors'

export function CodeSuggestionActions({
  suggestedChange,
  isAutomatedComment,
}: {
  suggestedChange: SuggestedChange
  isAutomatedComment?: boolean
}) {
  if (isFeatureEnabled('unified_batch_pr_comments')) {
    return <CommitSuggestionActions suggestedChange={suggestedChange} isAutomatedComment={isAutomatedComment} />
  }

  return <LegacyCodeSuggestionActions suggestedChange={suggestedChange} />
}

function LegacyCodeSuggestionActions({suggestedChange}: {suggestedChange: SuggestedChange}) {
  const setIsDialogOpen = useSetIsDialogOpen()
  const [isApplySuggestionsDialogOpen, setIsApplySuggestionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<RefObject<HTMLElement | null> | undefined>(undefined)
  const suggestionBatch = useSuggestionBatch()

  const isPendingInBatch =
    suggestedChange.commentDatabaseId !== undefined &&
    suggestedChange.commentDatabaseId !== null &&
    suggestionBatch.activeBatch.some(
      id =>
        id.commentDatabaseId === suggestedChange.commentDatabaseId &&
        id.suggestionIndex === suggestedChange.suggestionIndex,
    )

  // Validate using the batch which now stores threadId and pathLineRanges
  const {isValid, reason} = validateSuggestedChangeCanBeAddedToBatch(
    {
      threadId: suggestedChange.threadId,
      pathLineRanges: [{path: suggestedChange.path, lineRange: suggestedChange.lineRange}],
    },
    suggestionBatch.activeBatch,
  )

  const changesToApply = useMemo(() => [suggestedChange], [suggestedChange])

  const authorLogins = changesToApply.map((change: SuggestedChange) => change.authorLogin)

  const setDialogOpen = useCallback(
    (isDialogOpen: boolean) => {
      setIsApplySuggestionsDialogOpen(isDialogOpen)
      setIsDialogOpen?.(isDialogOpen)
    },
    [setIsDialogOpen],
  )

  const closeDialog = useCallback(() => {
    setDialogOpen(false)

    // manually return focus to the button that opened the dialog
    if (returnFocusRef.current) {
      const ref = returnFocusRef.current.current
      setTimeout(() => ref?.focus())
    }
  }, [setDialogOpen])

  const openDialog = useCallback(
    (returnFocusTo?: RefObject<HTMLElement | null>) => {
      setDialogOpen(true)

      // store a ref to an element we will return focus to when the dialog closes
      returnFocusRef.current = returnFocusTo
    },
    [setDialogOpen, returnFocusRef],
  )

  const {mutate: submitSuggestions} = useSubmitSuggestedChangesMutation()
  const {
    fullDiff: {headOid},
  } = useComparisonInfo()
  const handleCommit = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      submitSuggestions(
        {changes: changesToApply, message: commitMessage, currentOid: headOid},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'submitSuggestedChanges', 'Failed to submit suggested changes')
          },
        },
      )
    },
    [changesToApply, closeDialog, headOid, submitSuggestions],
  )

  return (
    <div className="border-top p-2">
      <ApplyOrAddToBatch suggestedChange={suggestedChange} onOpenDialog={openDialog} canAddToBatch={isValid} />

      {!isPendingInBatch && !isValid && reason && <CodeSuggestionUnavailable reason={reason} className="m-0" />}

      {isApplySuggestionsDialogOpen && (
        <ApplySuggestionDialog
          authorLogins={authorLogins}
          batchSize={changesToApply.length}
          commentKind="human"
          onClose={closeDialog}
          onCommit={handleCommit}
        />
      )}
    </div>
  )
}

function CommitSuggestionActions({
  suggestedChange,
  isAutomatedComment,
}: {
  suggestedChange: SuggestedChange
  isAutomatedComment?: boolean
}) {
  const setIsDialogOpen = useSetIsDialogOpen()
  const [isApplySuggestionsDialogOpen, setIsApplySuggestionsDialogOpen] = useState(false)
  const returnFocusRef = useRef<HTMLButtonElement>(null)
  const changesToApply = useMemo(() => [suggestedChange], [suggestedChange])
  const authorLogins = changesToApply.map((change: SuggestedChange) => change.authorLogin)

  const setDialogOpen = useCallback(
    (isDialogOpen: boolean) => {
      setIsApplySuggestionsDialogOpen(isDialogOpen)
      setIsDialogOpen?.(isDialogOpen)
    },
    [setIsDialogOpen],
  )

  const closeDialog = useCallback(() => {
    setDialogOpen(false)
    setTimeout(() => returnFocusRef.current?.focus())
  }, [setDialogOpen])
  const openDialog = useCallback(() => setDialogOpen(true), [setDialogOpen])

  const {mutate: submitSuggestions} = useSubmitSuggestedChangesMutation()
  const {
    fullDiff: {headOid},
  } = useComparisonInfo()
  const handleCommit = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      submitSuggestions(
        {changes: changesToApply, message: commitMessage, currentOid: headOid},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'submitSuggestedChanges', 'Failed to submit suggested changes')
          },
        },
      )
    },
    [changesToApply, closeDialog, headOid, submitSuggestions],
  )

  const suggestionActions =
    !isAutomatedComment && suggestedChange.commentDatabaseId != null ? (
      <SuggestionBatchControls
        suggestedChange={suggestedChange}
        commentDatabaseId={suggestedChange.commentDatabaseId}
        onOpenCommitDialog={openDialog}
        returnFocusRef={returnFocusRef}
      />
    ) : null

  return (
    <div className="border-top p-2">
      {suggestionActions ?? (
        <div className="d-flex flex-row flex-items-center gap-2 flex-justify-end">
          <Button variant="primary" onClick={openDialog} ref={returnFocusRef}>
            Commit suggestions
          </Button>
        </div>
      )}

      {isApplySuggestionsDialogOpen && (
        <ApplySuggestionDialog
          authorLogins={authorLogins}
          batchSize={changesToApply.length}
          commentKind="human"
          dialogTitle="Commit suggestions"
          onClose={closeDialog}
          onCommit={handleCommit}
          submitButtonLabel="Commit suggestions"
        />
      )}
    </div>
  )
}

function SuggestionBatchControls({
  suggestedChange,
  commentDatabaseId,
  onOpenCommitDialog,
  returnFocusRef,
}: {
  suggestedChange: SuggestedChange
  commentDatabaseId: number
  onOpenCommitDialog: () => void
  returnFocusRef: RefObject<HTMLButtonElement | null>
}) {
  const batchId = useMemo(
    () => ({
      commentDatabaseId,
      suggestionIndex: suggestedChange.suggestionIndex,
    }),
    [commentDatabaseId, suggestedChange.suggestionIndex],
  )
  const isInBatch = useIsInUnifiedBatch(batchId)
  const {addItem, removeItem} = useUnifiedBatchActions()
  const openManageBatchPanel = useOpenManageBatchPanel()
  const addToBatchButtonRef = useRef<HTMLButtonElement>(null)
  const threadHasBatchedSuggestion = useThreadHasBatchedSuggestion(suggestedChange.threadId)

  // Another suggestion from the same thread is already in the batch
  const threadHasOtherBatchedSuggestion = !isInBatch && threadHasBatchedSuggestion

  const prevThreadHasOtherBatchedSuggestionRef = useRef(threadHasOtherBatchedSuggestion)
  useEffect(() => {
    if (threadHasOtherBatchedSuggestion && !prevThreadHasOtherBatchedSuggestionRef.current) {
      announce(SUGGESTED_CHANGES_ERRORS.INVALID_RANGE)
    }
    prevThreadHasOtherBatchedSuggestionRef.current = threadHasOtherBatchedSuggestion
  }, [threadHasOtherBatchedSuggestion])

  const batchItem = useMemo(() => {
    return {
      commentGlobalId: suggestedChange.commentId,
      commentDatabaseId,
      path: suggestedChange.path,
      isOutdated: false,
      threadId: suggestedChange.threadId ?? null,
      hasSuggestion: true as const,
      lineRange: suggestedChange.lineRange,
      kind: 'human-with-suggestion' as const,
      suggestionIndex: suggestedChange.suggestionIndex,
    }
  }, [
    suggestedChange.commentId,
    commentDatabaseId,
    suggestedChange.lineRange,
    suggestedChange.path,
    suggestedChange.suggestionIndex,
    suggestedChange.threadId,
  ])

  const [addToBatchError, setAddToBatchError] = useState<string | undefined>()

  const handleAddToBatch = useCallback(() => {
    const result = addItem(batchItem)
    if (!result.ok) {
      setAddToBatchError(result.reason)
      announce(result.reason)
    } else {
      setAddToBatchError(undefined)
      openManageBatchPanel({returnFocusTo: addToBatchButtonRef.current})
    }
  }, [addItem, batchItem, openManageBatchPanel])

  const handleRemoveFromBatch = useCallback(() => {
    removeItem(batchId)
    setAddToBatchError(undefined)
  }, [removeItem, batchId])

  return (
    <>
      {threadHasOtherBatchedSuggestion ? (
        <CodeSuggestionUnavailable reason={SUGGESTED_CHANGES_ERRORS.INVALID_RANGE} className="m-0" />
      ) : (
        <div className="d-flex flex-row flex-items-center gap-2 flex-justify-end">
          <Button
            variant={isInBatch ? 'danger' : 'default'}
            onClick={isInBatch ? handleRemoveFromBatch : handleAddToBatch}
            ref={addToBatchButtonRef}
          >
            {isInBatch ? 'Remove from batch' : 'Add to batch'}
          </Button>
          {!isInBatch && (
            <Button variant="primary" onClick={onOpenCommitDialog} ref={returnFocusRef}>
              Commit suggestions
            </Button>
          )}
        </div>
      )}

      {addToBatchError && !threadHasOtherBatchedSuggestion && (
        <CodeSuggestionUnavailable reason={addToBatchError} className="m-0" />
      )}
    </>
  )
}
